import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { trackHeapEvent } from 'utils/analytics';
import CreateProfile1 from './Step1';
import CreateProfile2 from './Step2';
import CreateProfile3 from './Step3';
import CreateProfile4 from './Step4';
import { Button, message } from 'antd';
import s from './s.module.less';
import { getNextStep, getPrevStep } from 'pages/PPAOnboardingSteps/utils';
import { EPPAStep, ISaveBasicInfo } from 'types/ppaOnboarding';
import { savePPAOnboardingBasicInfo, saveStep } from 'api/ppaOnboarding';
import { IReviewData } from 'hooks/usePPAOnboardingReviewInfo';
import { encryptString } from 'utils/encrypt';
import { MODE } from 'constants/common';
import { getViewMode } from 'utils/sessionStorage';

interface IProps {
    setShowProgress: (show: boolean) => void;
    step?: EPPAStep;
    setStep: (step: EPPAStep) => void;
    reviewData: IReviewData;
    refetchReviewData?: () => void;
}

const formIds = {
    [EPPAStep.STEP_1]: 'name',
    [EPPAStep.STEP_2]: 'credentials',
    [EPPAStep.STEP_3]: 'gender',
};

const CreateProfile = ({ setShowProgress, step, setStep, reviewData, refetchReviewData }: IProps) => {
    const [nextButtonDisabled, setNextButtonDisabled] = useState(false);
    const [formId, setFormId] = useState<string>();
    const [submitLoading, setSubmitLoading] = useState(false);
    const [selectedGender, setSelectedGender] = useState<string | undefined>(reviewData?.basicInfo?.gender);
    const isOpsPreview = getViewMode() === MODE.OPS_PREVIEW;
    useEffect(() => {
        setFormId(formIds[step as keyof typeof formIds]);
    }, [step]);

    // Sync the pre-filled gender once review data arrives (without overriding a user's choice).
    useEffect(() => {
        if (reviewData?.basicInfo?.gender) {
            setSelectedGender((prev) => prev ?? reviewData.basicInfo?.gender);
        }
    }, [reviewData?.basicInfo?.gender]);

    useEffect(() => {
        if (step !== EPPAStep.STEP_1) {
            setShowProgress(true);
        }
    }, [setShowProgress, step]);

    const handleBack = useCallback(() => {
        if (step) {
            trackHeapEvent('WebApp - PPA Onboarding - CreateProfile - Click - Back', {
                step,
            });
            const newStep = getPrevStep(step);

            setStep(newStep);
        } else {
            console.error('step is undefined');
        }
    }, [step, setStep]);

    const handleSaveStep = useCallback(async () => {
        if (step) {
            try {
                setSubmitLoading(true);
                const nextStep = getNextStep(step as EPPAStep);
                if (isOpsPreview) {
                    setStep(nextStep);
                    refetchReviewData?.();
                    return;
                }
                const res = await saveStep(nextStep);
                if (res && !res.error) {
                    trackHeapEvent('WebApp - PPA Onboarding - CreateProfile - Click - Get Started Success', {
                        fromStep: step,
                        toStep: nextStep,
                    });
                    setStep(nextStep);
                    refetchReviewData?.();
                } else {
                    trackHeapEvent('WebApp - PPA Onboarding - CreateProfile - View - Get Started Failed', {
                        step,
                        error: res?.error,
                    });
                    message.error(res?.error);
                }
            } catch (error) {
                trackHeapEvent('WebApp - PPA Onboarding - CreateProfile - View - Get Started Error', {
                    step,
                    error: error instanceof Error ? error.message : 'Unknown error',
                });
                console.error(error);
            } finally {
                setSubmitLoading(false);
            }
        } else {
            console.error('step is undefined');
        }
    }, [setSubmitLoading, step, setStep, isOpsPreview]);

    const handleNext = useCallback(
        async (data: ISaveBasicInfo) => {
            if (isOpsPreview) {
                handleSaveStep();
                return;
            }
            try {
                setSubmitLoading(true);
                trackHeapEvent('WebApp - PPA Onboarding - CreateProfile - Click - Next Attempt', {
                    step,
                });
                const res = await savePPAOnboardingBasicInfo(data);
                if (res && !res.error) {
                    trackHeapEvent('WebApp - PPA Onboarding - CreateProfile - View - Next Success', {
                        step,
                    });
                    handleSaveStep();
                } else {
                    trackHeapEvent('WebApp - PPA Onboarding - CreateProfile - View - Next Failed', {
                        step,
                        error: res?.error,
                    });
                    message.error(res?.error);
                }
            } catch (error) {
                trackHeapEvent('WebApp - PPA Onboarding - CreateProfile - View - Next Error', {
                    step,
                    error: error instanceof Error ? error.message : 'Unknown error',
                });
                console.error(error);
            } finally {
                setSubmitLoading(false);
            }
        },
        [handleSaveStep, setSubmitLoading, isOpsPreview],
    );

    const commonProps = useMemo(() => {
        return {
            currentStep: step,
            setStep,
            setShowProgress,
            setNextButtonDisabled,
            setSubmitLoading,
            onNext: handleNext,
        };
    }, [handleNext, setShowProgress, setStep, step]);

    const contentRender = useMemo(() => {
        let content = null;

        if (step === EPPAStep.STEP_1) {
            content = <CreateProfile1 />;
        } else if (step === EPPAStep.STEP_2) {
            content = <CreateProfile2 initValue={reviewData.basicInfo} {...commonProps} formId={formId as string} />;
        } else if (step === EPPAStep.STEP_3) {
            content = <CreateProfile3 initValue={reviewData.basicInfo} {...commonProps} formId={formId as string} />;
        } else if (step === EPPAStep.STEP_4) {
            content = (
                <CreateProfile4
                    gender={selectedGender}
                    onChange={(v) => {
                        setSelectedGender(v);
                        handleNext({ gender: v });
                    }}
                />
            );
        } else {
            content = <CreateProfile1 />;
        }

        return content;
    }, [commonProps, formId, step, reviewData, selectedGender, handleNext]);

    const getCookieValue = (name: string) => {
        if (typeof document === 'undefined') return undefined;
        const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
        return match ? decodeURIComponent(match[1]) : undefined;
    };

    const addTHeapEvent = useCallback(async () => {
        const fbclid = getCookieValue('FBCLID');
        const email = reviewData?.basicInfo?.email;
        const firstName = reviewData?.basicInfo?.firstName;
        const lastName = reviewData?.basicInfo?.lastName;
        const firstService =
            Array.isArray(reviewData?.services) && reviewData?.services?.length ? reviewData.services[0] : undefined;
        const state = firstService?.state;
        const specialty = firstService?.serviceSpecialtyName;

        const [em, fn, ln, st] = await Promise.all([
            email ? encryptString(email.trim().toLowerCase()) : Promise.resolve(undefined),
            firstName ? encryptString(firstName.trim().toLowerCase()) : Promise.resolve(undefined),
            lastName ? encryptString(lastName.trim().toLowerCase()) : Promise.resolve(undefined),
            state ? encryptString(state.trim().toLowerCase()) : Promise.resolve(undefined),
        ]);

        const payload = {
            step,
            FBCLID: fbclid,
            event_type: 'Lead',
            stage: 'account_created',
            em,
            fn,
            ln,
            st,
            specialty,
            application_id:
                typeof globalThis !== 'undefined' &&
                globalThis.crypto &&
                typeof globalThis.crypto.randomUUID === 'function'
                    ? globalThis.crypto.randomUUID()
                    : undefined,
            timestamp: new Date().toISOString(),
        } as Record<string, any>;

        trackHeapEvent('WebApp - PPA Onboarding - CreateProfile - Click - Get Started', payload);
    }, [reviewData, step]);

    const footerBtnsRender = useMemo(() => {
        if (step === EPPAStep.STEP_1) {
            return (
                <Button
                    type="primary"
                    onClick={() => {
                        handleSaveStep();
                        addTHeapEvent();
                    }}
                >
                    Get started
                </Button>
            );
        } else if (step === EPPAStep.STEP_4) {
            return (
                <>
                    <Button onClick={handleBack}>Back</Button>
                    <Button
                        loading={submitLoading}
                        type="primary"
                        disabled={!selectedGender}
                        onClick={() => handleNext({ gender: selectedGender as string })}
                    >
                        Next
                    </Button>
                </>
            );
        } else {
            return (
                <>
                    <Button onClick={handleBack}>Back</Button>
                    <Button
                        loading={submitLoading}
                        type="primary"
                        htmlType="submit"
                        form={formIds[step as keyof typeof formIds]}
                        disabled={nextButtonDisabled && !isOpsPreview}
                    >
                        Next
                    </Button>
                </>
            );
        }
    }, [
        step,
        handleSaveStep,
        handleBack,
        handleNext,
        submitLoading,
        nextButtonDisabled,
        selectedGender,
        addTHeapEvent,
    ]);
    return (
        <div className={s.wrap}>
            {contentRender}
            <div className={`${s.footer} ${step === EPPAStep.STEP_1 ? s.getStartedFooter : ''}`}>
                {footerBtnsRender}
            </div>
        </div>
    );
};

export default CreateProfile;
